// Total calculado de una escala propia (PHQ-9, Glasgow) dentro del motor de Questionnaire — ver totalCalculado.ts. Solo lectura y
// siempre al día con las respuestas; "Escribir a mano" lo deja editable (validado contra el rango) y desmarcarlo vuelve al calculado.
// Una respuesta guardada que no coincide con el cálculo (datos viejos, tecleados a mano) se abre como "a mano": nunca se pisa.
import type { QuestionnaireItem, QuestionnaireResponse, QuestionnaireResponseItemAnswer } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { opcionDeInterpretacion, rangoDeTotal, sumaDeFuentes, totalDeInterpretacion, totalEnRango, valorNumericoDe } from './totalCalculado';

const ETIQUETA_SECCION = 'block text-xs font-mono uppercase tracking-wide text-hce-muted';

type Cambiar = (respuesta: QuestionnaireResponseItemAnswer[]) => void;

export function TotalCalculado({ item, fuentes, respuesta, actual, onChange }: { readonly item: QuestionnaireItem; readonly fuentes: readonly string[]; readonly respuesta: QuestionnaireResponse; readonly actual: number | undefined; readonly onChange: Cambiar }): JSX.Element {
  const { t } = useTranslation();
  const calculado = sumaDeFuentes(respuesta, fuentes);
  const [aMano, setAMano] = useState(() => actual !== undefined && actual !== calculado);
  const { min, max } = rangoDeTotal(item);

  useEffect(() => {
    if (aMano) return;
    const objetivo = totalEnRango(item, calculado) ? calculado : undefined;
    if (objetivo !== actual) onChange(objetivo === undefined ? [] : [{ valueInteger: objetivo }]);
  }, [aMano, calculado, actual, item, onChange]);

  const fueraDeRango = aMano && actual !== undefined && !totalEnRango(item, actual);

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={item.linkId} className={ETIQUETA_SECCION}>
        {item.text}
        {item.required && <span className="text-hce-clay"> *</span>}
      </Label>
      <Input
        id={item.linkId}
        type="number"
        step={1}
        min={min}
        max={max}
        aria-readonly={!aMano}
        required={item.required}
        value={actual ?? ''}
        onChange={(e) => {
          if (aMano) onChange(e.target.value === '' ? [] : [{ valueInteger: parseInt(e.target.value, 10) }]);
        }}
        className={aMano ? undefined : 'bg-hce-surface'}
      />
      <div className="flex items-center gap-2">
        <Checkbox id={`${item.linkId}-a-mano`} checked={aMano} onCheckedChange={(v) => setAMano(v === true)} />
        <Label htmlFor={`${item.linkId}-a-mano`} className="text-sm font-normal text-hce-ink">
          {t('formularios.uci.balance.escribirAMano')}
        </Label>
      </div>
      {fueraDeRango && (
        <p className="text-sm text-hce-clay">
          {t('formularios.escalaTotal.fueraDeRango', { min, max })}
        </p>
      )}
      {!aMano && calculado === undefined && <p className="text-xs text-hce-muted">{t('formularios.escalaTotal.faltaResponder')}</p>}
    </div>
  );
}

/** Mantiene la interpretación (choice) de acuerdo con el total; no dibuja nada. */
export function InterpretacionAutomatica({ item, respuesta, actualCodigo, onChange }: { readonly item: QuestionnaireItem; readonly respuesta: QuestionnaireResponse; readonly actualCodigo: string | undefined; readonly onChange: Cambiar }): null {
  const totalId = totalDeInterpretacion(item);
  const total = totalId ? valorNumericoDe(respuesta, totalId) : undefined;
  const opcion = opcionDeInterpretacion(item, total);
  const codigo = opcion?.code;
  const display = opcion?.display;
  useEffect(() => {
    if (!totalId) return;
    if (codigo === actualCodigo) return;
    onChange(codigo ? [{ valueCoding: { code: codigo, display } }] : []);
  }, [totalId, codigo, display, actualCodigo, onChange]);
  return null;
}
